import UserStatusCircleProgress from '../../UserStatusCircleProgress/UserStatusCircleProgress';
import React, { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getAppUser, getUserPaymentAccountCode } from '../../../../app/appSelectors';
import {
    UserStatusWithdrawalLimitsHistoryHeaderStyled,
    UserStatusWithdrawalLimitsHistoryStyled,
    UserStatusWithdrawalLimitsStatusContainerStyled,
    UserStatusWithdrawalLimitsStatusInfoStyled,
    UserStatusWithdrawalLimitsStyled,
} from './styled/UserStatusWithdrawalLimitsStyled';
import { useLangContext } from '../../../../providers/LangProvider/LangProviderContext';
import _ from 'lodash';
import UserStatusWithdrawalLimitsNumbers from './UserStatusWithdrawalLimitsNumbers';
import { Button } from 'react-bootstrap';
import { LOCATION_NAMES } from '../../../Location/locationConstants';
import { locationSlice } from '../../../Location/locationReducer';
import { COMSA_FEE_PERCENT } from '../../../Withdrawal/withdrawalConstants';
import UserStatusWithdrawalLimitsProgressCard from './UserStatusWithdrawalLimitsProgressCard';
import { getPayoutParams } from '../../../Withdrawal/withdrawalSelectors';
import moment from 'moment';
import MobileHistoryContent
    from '../../../../mobileAppCore/components/MobileHistory/MobileHistoryContent/MobileHistoryContent';
import { loadAccountHistoryAction } from '../../../../app/appActions';
import { getAccountHistory } from '../../../Account/accountSelectors';
import { TYPE_TX } from '../../../Account/components/TransactionHistory/transactionHistoryConstants';
import SeeAllButton from '../../../../components/SeeAllButton/SeeAllButton';
import { TTransactionType } from '../../../Account/types/accountActions.t';

function UserStatusWithdrawalLimits() {
    const user = useSelector(getAppUser);
    const { getLangPath } = useLangContext();
    const dispatch = useDispatch();
    const payoutParams = useSelector(getPayoutParams);
    const paymentAccountCode = useSelector(getUserPaymentAccountCode);
    const history = useSelector(getAccountHistory);

    useEffect(() => {
        if (paymentAccountCode) {
            dispatch(loadAccountHistoryAction({
                accountCode: paymentAccountCode,
                type: 'transaction',
            }));
        }
    }, [paymentAccountCode, dispatch]);

    const capitalizedStatus = useMemo(() => {
        return _.capitalize(user.user_status);
    }, [user]);

    const onAboutStatusClickHandler = useCallback(() => {
        dispatch(locationSlice.actions.openLocationAction({ name: LOCATION_NAMES.USER_STATUSES, method: 'replace' }));
    }, [dispatch]);

    const dayLimitUpdateDate = useMemo(() => {
        return moment().utc().startOf('day').add(1, 'day').format('DD.MM.YYYY UTC');
    }, []);

    const monthLimitUpdateDate = useMemo(() => {
        return moment().utc().startOf('month').add(1, 'month').format('DD.MM.YYYY UTC');
    }, []);

    const historyTransactions = useMemo(() => {
        return history.transactions.filter(transaction =>
            ([TYPE_TX.WITHDRAW, TYPE_TX.COMMISSION] as TTransactionType[]).includes(transaction.type));
    }, [history]);

    const onSeeAllClickHandler = useCallback(() => {
        dispatch(locationSlice.actions.openLocationAction({
                name: LOCATION_NAMES.HISTORY,
                searchParams: { transactions: 'true' },
            }),
        );
    }, [dispatch]);

    return <UserStatusWithdrawalLimitsStyled>
        <UserStatusWithdrawalLimitsStatusContainerStyled>
            <UserStatusCircleProgress
                size={80}
                progress={user.user_turnover_percent}
            />
            <UserStatusWithdrawalLimitsStatusInfoStyled isUnlimited={!user.withdrawalLimits}>
                <h5>{getLangPath('user_statuses.withdrawal_limits.status_limits', { status: capitalizedStatus })}</h5>
                <UserStatusWithdrawalLimitsNumbers withdrawalLimits={user.withdrawalLimits}/>
            </UserStatusWithdrawalLimitsStatusInfoStyled>
        </UserStatusWithdrawalLimitsStatusContainerStyled>

        <Button
            onClick={onAboutStatusClickHandler}
            variant={'secondary'}
        >
            {getLangPath('user_statuses.withdrawal_limits.about_status')}
        </Button>

        <span className={'text-secondary'}>
            {getLangPath('user_statuses.withdrawal_limits.description', { commission_percent: `${COMSA_FEE_PERCENT}` })}
        </span>

        <UserStatusWithdrawalLimitsProgressCard
            limit={user.withdrawalLimits?.dailyLimitUsd}
            limitUpdateDate={dayLimitUpdateDate}
            periodCurrentAmount={payoutParams.currentDayPayoutAmountProcessedWithoutCommissions}
            title={getLangPath('user_statuses.withdrawal_limits.daily_withdrawal')}
        />

        <UserStatusWithdrawalLimitsProgressCard
            limit={user.withdrawalLimits?.monthlyLimitUsd}
            limitUpdateDate={monthLimitUpdateDate}
            periodCurrentAmount={payoutParams.currentMonthPayoutAmountProcessedWithoutCommissions}
            title={getLangPath('user_statuses.withdrawal_limits.monthly_withdrawal')}
        />

        <UserStatusWithdrawalLimitsHistoryStyled>
            <UserStatusWithdrawalLimitsHistoryHeaderStyled>
                <span className={'text-secondary'}>{getLangPath('user_statuses.withdrawal_limits.history')}</span>
                <SeeAllButton onClick={onSeeAllClickHandler}/>
            </UserStatusWithdrawalLimitsHistoryHeaderStyled>
            <MobileHistoryContent
                transactions={historyTransactions}
            />
        </UserStatusWithdrawalLimitsHistoryStyled>
    </UserStatusWithdrawalLimitsStyled>;
}

export default UserStatusWithdrawalLimits;
